import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Button, Card, CardContent, Input } from '@jeraisonne/ui';
import { Search, UserMinus, X } from 'lucide-react';
import { AvatarCircle } from '@/components/avatar-circle';
import {
  useFollowedTeachers,
  useFollowTeacher,
  useUnfollowTeacher,
} from '@/lib/student/teacher-follows';
import { useSearchTutors } from '@/lib/student/search-tutors';
import { filterByQuery, hasSearchQuery } from '@/lib/contact-search';

interface Props {
  studentId: string;
  /** Recherche de la page « Mes contacts » : ne montre que les profs suivis correspondants. */
  filter?: string;
}

export function FollowedTeachersSection({ studentId, filter = '' }: Props) {
  const { data: followed = [], isLoading } = useFollowedTeachers(studentId);
  const shown = filterByQuery(followed, filter, (teacher) => [teacher.display_name]);
  const followedIds = new Set(followed.map((t) => t.id));
  const unfollow = useUnfollowTeacher(studentId);
  const follow = useFollowTeacher(studentId);

  const [showSearch, setShowSearch] = useState(false);
  const [query, setQuery] = useState('');
  const [pendingFollowId, setPendingFollowId] = useState<string | null>(null);
  const [errorMap, setErrorMap] = useState<Record<string, string>>({});
  const { data: results = [], isLoading: searching } = useSearchTutors(query);

  function handleFollow(tutorId: string) {
    setPendingFollowId(tutorId);
    setErrorMap((m) => {
      const next = { ...m };
      delete next[tutorId];
      return next;
    });
    follow.mutate(tutorId, {
      onError: (e) =>
        setErrorMap((m) => ({ ...m, [tutorId]: (e as Error).message || 'Erreur inconnue' })),
      onSettled: () => setPendingFollowId((cur) => (cur === tutorId ? null : cur)),
    });
  }

  // Pendant une recherche, une liste sans résultat disparaît — sauf si le panneau « Suivre un prof » est ouvert.
  if (!isLoading && hasSearchQuery(filter) && shown.length === 0 && !showSearch) return null;

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-ink-400 text-xs font-semibold uppercase tracking-wider">
          Profs suivis ({shown.length})
        </p>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setShowSearch((v) => !v)}
          className="gap-1.5"
        >
          {showSearch ? <X className="h-4 w-4" /> : <Search className="h-4 w-4" />}
          {showSearch ? 'Fermer' : 'Suivre un prof'}
        </Button>
      </div>

      {showSearch && (
        <Card className="border-ink-200">
          <CardContent className="space-y-3 py-4">
            <div className="relative">
              <Search className="text-ink-400 absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Cherche un prof par nom…"
                className="pl-9"
              />
            </div>
            {query.trim().length >= 2 && (
              <div className="space-y-2">
                {searching ? (
                  <p className="text-ink-400 text-sm">Recherche…</p>
                ) : results.length === 0 ? (
                  <p className="text-ink-400 text-sm">Aucun résultat.</p>
                ) : (
                  results.map((tutor) => {
                    const isFollowed = followedIds.has(tutor.id);
                    const isPending = pendingFollowId === tutor.id && follow.isPending;
                    const error = errorMap[tutor.id];
                    return (
                      <div
                        key={tutor.id}
                        className="border-ink-100 space-y-1 rounded-xl border bg-white p-3"
                      >
                        <div className="flex items-center gap-3">
                          <AvatarCircle
                            name={tutor.display_name}
                            src={tutor.avatar_url}
                            size="sm"
                          />
                          <p className="text-ink-900 min-w-0 flex-1 truncate text-sm font-semibold">
                            {tutor.display_name}
                          </p>
                          <Button
                            size="sm"
                            variant={isFollowed ? 'outline' : 'primary'}
                            disabled={isFollowed || isPending}
                            onClick={() => handleFollow(tutor.id)}
                          >
                            {isFollowed ? 'Déjà suivi' : isPending ? '…' : 'Suivre'}
                          </Button>
                        </div>
                        {error && <p className="text-accent-600 pl-12 text-xs">{error}</p>}
                      </div>
                    );
                  })
                )}
              </div>
            )}
            {query.trim().length > 0 && query.trim().length < 2 && (
              <p className="text-ink-400 text-xs">Saisis au moins 2 caractères.</p>
            )}
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <Card className="border-ink-100 animate-pulse">
          <CardContent className="flex items-center gap-3 py-3">
            <div className="bg-ink-100 h-10 w-10 rounded-full" />
            <div className="bg-ink-100 h-4 w-32 rounded" />
          </CardContent>
        </Card>
      ) : followed.length === 0 ? (
        !showSearch && (
          <Card className="border-ink-100 border-dashed">
            <CardContent className="py-6 text-center">
              <p className="text-ink-500 text-sm font-medium">Tu ne suis aucun prof</p>
              <p className="text-ink-400 mt-1 text-xs">
                Suis un prof pour voir ses activités dans ton fil d'actualités.
              </p>
            </CardContent>
          </Card>
        )
      ) : (
        <div className="space-y-2">
          {shown.map((teacher) => (
            <Card
              key={teacher.followId}
              className="border-ink-100 hover:bg-ink-50/40 transition-colors"
            >
              <CardContent className="flex items-center gap-3 py-3">
                <Link
                  to="/tutors/$tutorId"
                  params={{ tutorId: teacher.id }}
                  className="flex min-w-0 flex-1 items-center gap-3"
                >
                  <AvatarCircle name={teacher.display_name} src={teacher.avatar_url} size="sm" />
                  <p className="text-ink-900 truncate text-sm font-semibold hover:underline">
                    {teacher.display_name}
                  </p>
                </Link>
                <button
                  type="button"
                  onClick={() => unfollow.mutate(teacher.id)}
                  disabled={unfollow.isPending}
                  className="text-ink-400 flex h-8 items-center gap-1 rounded-full px-2 text-xs font-medium transition-colors hover:text-red-500"
                  aria-label={`Ne plus suivre ${teacher.display_name}`}
                >
                  <UserMinus className="h-3.5 w-3.5" />
                  Ne plus suivre
                </button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </section>
  );
}
